import { humanizeKey } from "./format";

// `/insights/me/*` errors for non-artists: 403, an explicit code, or a
// message mentioning "artist".
export function isNotArtistError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data || {};
  const code = String(data.code || data.error || "").toUpperCase();
  const message = String(data.message || error?.message || "");
  if (code.includes("NOT_AN_ARTIST") || code.includes("NOT_ARTIST")) return true;
  if (/not\s+an?\s+artist|artists?\s+only/i.test(message)) return true;
  return status === 403;
}

// Payloads may be a bare list or wrapped ({ streams: [] }, { items: [] }...).
export function asList(result, ...keys) {
  if (Array.isArray(result)) return result;
  if (!result || typeof result !== "object") return [];
  for (const key of [...keys, "items", "list", "data", "results"]) {
    if (Array.isArray(result[key])) return result[key];
  }
  return [];
}

// Headline metrics arrive either as a list [{key,label,value,previous,delta}]
// or a map { listeners: { value, previous, delta } } / { listeners: 12 }.
// Returns [{ key, label, value, previous, delta }] with delta in percent.
export function normalizeMetrics(metrics) {
  const rows = Array.isArray(metrics)
    ? metrics
    : metrics && typeof metrics === "object"
      ? Object.keys(metrics).map((key) => {
          const m = metrics[key];
          return m && typeof m === "object" ? { key, ...m } : { key, value: m };
        })
      : [];
  return rows
    .filter((m) => m && (m.key || m.label))
    .map((m) => {
      const value = Number(m.value ?? m.current ?? 0);
      const previous = m.previous ?? m.prev;
      let delta = m.delta ?? m.deltaPct;
      if (delta === undefined && previous !== undefined) {
        const prev = Number(previous);
        delta = prev === 0 ? (value === 0 ? 0 : 100) : ((value - prev) / Math.abs(prev)) * 100;
      }
      return {
        key: m.key || m.label,
        label: m.label || humanizeKey(m.key),
        value: Number.isFinite(value) ? value : 0,
        previous: previous === undefined ? null : Number(previous),
        delta: delta === undefined || delta === null ? null : Number(delta),
      };
    });
}

// Milestones: { reached: [], next: [] } or a flat list with `reachedAt`.
export function splitMilestones(milestones) {
  if (!milestones) return { reached: [], next: [] };
  if (Array.isArray(milestones)) {
    return {
      reached: milestones.filter((m) => m && (m.reachedAt || m.reached)),
      next: milestones.filter((m) => m && !(m.reachedAt || m.reached)),
    };
  }
  return {
    reached: asList(milestones, "reached", "achieved"),
    next: asList(milestones, "next", "upcoming"),
  };
}

export function milestoneLabel(m) {
  if (!m) return "";
  const kind = m.kind || m.type || m.metric || "";
  const value = m.value ?? m.target ?? m.threshold;
  const nice = Number.isFinite(Number(value)) ? Number(value).toLocaleString("en-US") : value;
  return `${nice} ${humanizeKey(kind).toLowerCase()}`.trim();
}
